import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import Navbar from "@Components/Navbar/Navbar";
import V2Footer from "@Components/V2Footer/V2Footer";
import { logAppEvent } from "@Utils/appAnalytics";
import { APP_EVENTS, APP_PAGE } from "@Utils/webApputils/analyticEvents";
import { ABOUT_URL, BLOG_URL } from "constants/configConstants";
import Hero from "./components/Hero/Hero";
import ReelsRail from "./components/ReelsRail/ReelsRail";
import Benefits from "./components/Benefits/Benefits";
import Steps from "./components/Steps/Steps";
import Voices from "./components/Voices/Voices";
import Cta from "./components/Cta/Cta";
import styles from "./AmbassadorV2.module.scss";
import useTranslationHook from "hooks/useTranslationHook";

/**
 * amber Ambassador — v2.
 *
 * Structure: headline, one line of copy, one CTA, then a bleeding rail of
 * creator reels, then why to join, then the three joining steps, then
 * ambassadors' own words, then the closing band. The hero states the offer, the
 * rail is the evidence for it, "why join" makes the case, and the steps say how
 * to take it up — the argument before the process.
 *
 * ── What carries over from the page this replaces ──────────────────────────
 * The apply CTA still opens the programme's Typeform (see ./constants), and the
 * PAGE_VIEWED event still fires on mount. Those are the two things the old page
 * actually did.
 *
 * ── What does NOT carry over ───────────────────────────────────────────────
 * Its four sections (three-steps, why-join, words-from-ambassadors,
 * ready-to-join) are gone, and their components are not reused — this page is
 * built from its own, per the brief. `pages/Ambassador` is left untouched on
 * disk until this replaces it everywhere.
 *
 * ⚠️  THE RAIL'S DATA IS A SNAPSHOT, NOT A FEED. See components/ReelsRail/reels.ts:
 * eight real reels with handles, follower counts and poster frames harvested
 * from each post's own Instagram embed in Sep 2026. Follower counts drift, and
 * two of the ten supplied reels were dropped because Instagram's collab embeds
 * expose no poster frame — the reasons are recorded there.
 */
const AmbassadorV2 = () => {
  const { t } = useTranslationHook();
  const { lastScreen, isUserLoggedIn } = useSelector((state: AppState) => ({
    lastScreen: state.appData.lastScreen,
    isUserLoggedIn: state.appData.isUserLoggedIn,
  }));

  // Same payload shape the page this replaces sent, so the ambassador funnel
  // stays comparable either side of the switch.
  useEffect(() => {
    logAppEvent({
      eventName: APP_EVENTS.PAGE_VIEWED,
      payload: {
        screen: APP_PAGE.AMBASSADOR,
        previous_screen: lastScreen,
        is_logged_in: isUserLoggedIn,
      },
    });
  }, []);

  return (
    <div className={styles.page}>
      <Helmet title={t("ambassador.v2.meta.title")}>
        <meta name="description" content={t("ambassador.v2.meta.description")} />
        {/* Instrument Sans — the design font, loaded only on this page */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&display=swap"
        />
      </Helmet>

      <Navbar
        links={[
          { label: "navbar.v2.blogs", href: BLOG_URL },
          { label: "navbar.v2.aboutus", href: ABOUT_URL },
        ]}
      />

      <Hero />
      <ReelsRail />
      {/* Benefits ("Why should you join") sits ABOVE Steps: the page argues the
          case before explaining the process. Both bands are #fff, so the order
          is free of any background-sequence constraint. */}
      <Benefits />
      <Steps />
      <Voices />
      <Cta />

      <V2Footer />
    </div>
  );
};

export default wrapperHOC(AmbassadorV2, {
  componentName: "AmbassadorV2",
  showForChina: true,
});
